import { action, computed, observable } from 'mobx'
import { type IValidationSchema, validationContext } from 'validx'
import {
  mustHaveItems,
  validateWhileEditing,
} from '@/features/legends/editor/validation'
import {
  FilterEditorViewModelBase,
  type FilterEditorViewModelOptions,
} from '@/features/legends/filters/FilterEditorViewModelBase'
import type {
  ConjunctionOperator,
  HasSkillsRule,
} from '@/features/legends/filters/rules'

/**
 * Passes when the pro has all (or any) of the skills.
 */
export class HasSkillsEditorViewModel extends FilterEditorViewModelBase<HasSkillsRule> {
  private readonly _schema: IValidationSchema<HasSkillsEditorViewModel> = {
    skills: [mustHaveItems('Please select at least 1 skill.')],
  }

  readonly type = 'HasSkills'

  @observable accessor skills: (string | number)[] = []
  @observable accessor op: ConjunctionOperator = 'All'

  readonly validation = validationContext(this, this._schema)

  constructor(options: FilterEditorViewModelOptions) {
    super(options)
    this.lifecycle.manage(validateWhileEditing(this, this._schema))
  }

  /**
   * The workspace's skills, as options to pick from.
   */
  @computed
  get availableSkills() {
    return this.dependencies.catalog.skills.map((x) => ({
      id: x.id,
      text: x.name,
    }))
  }

  /**
   * Imported legends reference skills by name, so a stored value may be a
   * name or an ID, and older ones store numeric IDs. Resolves all of them to
   * IDs.
   */
  @computed
  get selectedSkillIds(): string[] {
    return this.skills
      .map((idOrName) =>
        this.dependencies.catalog.skills.find(
          (s) => String(s.id) === String(idOrName) || s.name === idOrName,
        ),
      )
      .filter((s) => !!s)
      .map((s) => s.id)
  }

  /**
   * Sets the skills by ID.
   */
  @action.bound
  setSkills(skills: string[]) {
    this.skills = skills
  }

  /**
   * Sets whether the pro needs all or any of the skills.
   */
  @action.bound
  setOp(op: ConjunctionOperator) {
    this.op = op
  }

  /** @inheritDoc */
  @action
  hydrate({ skills, op }: HasSkillsRule) {
    this.skills = skills ?? []
    if (op) {
      this.op = op
    }
  }

  /** @inheritDoc */
  toJSON(): HasSkillsRule {
    return { type: this.type, skills: this.skills.slice(), op: this.op }
  }
}
